import { Settings, getVideoCodecOverride, resolveVideoCodecs, } from '@streamr/tv-engine/~/settings';
import { proxy, transfer, wrap } from 'comlink';
import { orientationTypeToRotationMap } from '../../utils/getIosFrameRotation';
import { createWorker } from '../../utils/workerUtils';
import { collectSceneTransferables } from './types/Scene';
export class AbstractVideoEngine {
    optimalDelayCallback;
    encodedVideoOutputTrackAddedCallback;
    // Top-level workers (all on-demand)
    videoPlayerWorker;
    videoPlayerWorkerProxy;
    videoEncoderWorker;
    videoEncoderWorkerProxy;
    videoDecoderWorker;
    videoDecoderWorkerProxy;
    timerWorker;
    timerWorkerProxy;
    pendingDisplayCanvas;
    async init(optimalDelayCallback, encodedVideoOutputTrackAddedCallback) {
        this.optimalDelayCallback = optimalDelayCallback;
        this.encodedVideoOutputTrackAddedCallback =
            encodedVideoOutputTrackAddedCallback;
        await this.onInit();
        console.log(`${this.LOG_PREFIX} init`);
    }
    async destroy() {
        await this.onDestroy();
        if (this.videoEncoderWorkerProxy) {
            await this.stopVideoEncoderWorker();
        }
        if (this.videoDecoderWorkerProxy) {
            await this.stopVideoDecoderWorker();
        }
        if (this.videoPlayerWorkerProxy) {
            await this.stopVideoPlayerWorker();
        }
        this.destroyTimerWorker();
        console.log(`${this.LOG_PREFIX} destroy`);
    }
    async onDestroy() {
        // Default implementation does nothing
    }
    async onInit() {
        // Default implementation does nothing
    }
    // ========== Per-worker lifecycle ==========
    async startVideoPlayerWorker() {
        if (this.videoPlayerWorkerProxy) {
            console.warn(`${this.LOG_PREFIX} videoPlayerWorker already running`);
            return;
        }
        this.videoPlayerWorker = this.createPlayerWorker();
        this.videoPlayerWorkerProxy = wrap(this.videoPlayerWorker);
        if (!this.optimalDelayCallback) {
            throw new Error('init() must be called before starting workers');
        }
        await this.videoPlayerWorkerProxy.init(proxy(this.optimalDelayCallback));
        await this.wireTimerPort('player');
        if (this.pendingDisplayCanvas) {
            console.log(`${this.LOG_PREFIX} applying deferred display canvas`);
            await this.setDisplayCanvas(this.pendingDisplayCanvas);
        }
        console.log(`${this.LOG_PREFIX} videoPlayerWorker started`);
    }
    async stopVideoPlayerWorker() {
        try {
            await this.videoPlayerWorkerProxy?.destroy();
        }
        catch (error) {
            console.warn(`${this.LOG_PREFIX} videoPlayerWorker destroy failed`, error);
        }
        this.videoPlayerWorker?.terminate();
        this.videoPlayerWorkerProxy = undefined;
        this.videoPlayerWorker = undefined;
        console.log(`${this.LOG_PREFIX} videoPlayerWorker stopped`);
    }
    async startVideoDecoderWorker() {
        if (this.videoDecoderWorkerProxy) {
            console.warn(`${this.LOG_PREFIX} videoDecoderWorker already running`);
            return;
        }
        if (!this.videoPlayerWorkerProxy) {
            throw new Error('videoPlayerWorker must be running before starting videoDecoderWorker');
        }
        this.videoDecoderWorker = this.createDecoderWorker();
        this.videoDecoderWorkerProxy = wrap(this.videoDecoderWorker);
        await this.videoDecoderWorkerProxy.init();
        const decoderPlayerChannel = new MessageChannel();
        await this.videoDecoderWorkerProxy.connectPlayerPort(transfer(decoderPlayerChannel.port1, [decoderPlayerChannel.port1]));
        await this.videoPlayerWorkerProxy.connectDecoderPort(transfer(decoderPlayerChannel.port2, [decoderPlayerChannel.port2]));
        console.log(`${this.LOG_PREFIX} videoDecoderWorker started`);
    }
    async stopVideoDecoderWorker() {
        try {
            await this.videoDecoderWorkerProxy?.destroy();
        }
        catch (error) {
            console.warn(`${this.LOG_PREFIX} videoDecoderWorker destroy failed`, error);
        }
        this.videoDecoderWorker?.terminate();
        this.videoDecoderWorkerProxy = undefined;
        this.videoDecoderWorker = undefined;
        console.log(`${this.LOG_PREFIX} videoDecoderWorker stopped`);
    }
    async startVideoEncoderWorker() {
        if (this.videoEncoderWorkerProxy) {
            console.warn(`${this.LOG_PREFIX} videoEncoderWorker already running`);
            return;
        }
        if (!this.videoPlayerWorkerProxy) {
            throw new Error('videoPlayerWorker must be running before starting videoEncoderWorker');
        }
        this.videoEncoderWorker = this.createEncoderWorker();
        this.videoEncoderWorkerProxy = wrap(this.videoEncoderWorker);
        if (!this.encodedVideoOutputTrackAddedCallback) {
            throw new Error('init() must be called before starting workers');
        }
        await this.videoEncoderWorkerProxy.init(proxy(this.encodedVideoOutputTrackAddedCallback));
        const codecOverride = getVideoCodecOverride();
        if (codecOverride) {
            await this.videoEncoderWorkerProxy.setVideoCodecOverride(codecOverride);
        }
        const encoderPlayerChannel = new MessageChannel();
        await this.videoEncoderWorkerProxy.connectPlayerPort(transfer(encoderPlayerChannel.port1, [encoderPlayerChannel.port1]));
        await this.videoPlayerWorkerProxy.connectEncoderPort(transfer(encoderPlayerChannel.port2, [encoderPlayerChannel.port2]));
        await this.wireTimerPort('encoder');
        await this.onVideoEncoderWorkerStarted();
        console.log(`${this.LOG_PREFIX} videoEncoderWorker started`);
    }
    async onVideoEncoderWorkerStarted() {
        // Subclass hook (e.g. StandardVideoEngine connects the extractor)
    }
    setPerTrackVideoEncodingEnabled(enabled) {
        this.videoEncoderWorkerProxy?.setPerTrackEncodingEnabled(enabled);
    }
    async connectEncoderNetworkingPort(port) {
        if (!this.videoEncoderWorkerProxy) {
            throw new Error('VideoEncoderWorker not running');
        }
        await this.videoEncoderWorkerProxy.connectNetworkingPort(transfer(port, [port]));
    }
    async connectCompositeNetworkingPort(port) {
        if (!this.videoEncoderWorkerProxy) {
            throw new Error('VideoEncoderWorker not running');
        }
        await this.videoEncoderWorkerProxy.connectCompositeNetworkingPort(transfer(port, [port]));
    }
    async connectDecoderNetworkingPort(port) {
        if (!this.videoDecoderWorkerProxy) {
            throw new Error('VideoDecoderWorker not running');
        }
        await this.videoDecoderWorkerProxy.connectNetworkingPort(transfer(port, [port]));
    }
    async stopVideoEncoderWorker() {
        await this.onVideoEncoderWorkerStopping();
        try {
            await this.videoEncoderWorkerProxy?.destroy();
        }
        catch (error) {
            console.warn(`${this.LOG_PREFIX} videoEncoderWorker destroy failed`, error);
        }
        this.videoEncoderWorker?.terminate();
        this.videoEncoderWorkerProxy = undefined;
        this.videoEncoderWorker = undefined;
        console.log(`${this.LOG_PREFIX} videoEncoderWorker stopped`);
    }
    async onVideoEncoderWorkerStopping() {
        // Subclass hook
    }
    isVideoPlayerWorkerRunning() {
        return this.videoPlayerWorkerProxy !== undefined;
    }
    isVideoDecoderWorkerRunning() {
        return this.videoDecoderWorkerProxy !== undefined;
    }
    isVideoEncoderWorkerRunning() {
        return this.videoEncoderWorkerProxy !== undefined;
    }
    setPerTrackVideoBitrate(trackId, bitrate) {
        this.videoEncoderWorkerProxy?.setTrackBitrate(trackId, bitrate);
    }
    // ========== Display & playback ==========
    async setDisplayCanvas(canvas) {
        if (!this.videoPlayerWorkerProxy) {
            console.log(`${this.LOG_PREFIX} setDisplayCanvas: deferring (player worker not running)`);
            this.pendingDisplayCanvas = canvas;
            return;
        }
        this.pendingDisplayCanvas = undefined;
        const offscreenCanvas = canvas.transferControlToOffscreen();
        await this.videoPlayerWorkerProxy.setDisplayCanvas(transfer(offscreenCanvas, [offscreenCanvas]));
        console.log(`${this.LOG_PREFIX} setDisplayCanvas`);
    }
    async setLiveSceneThumbnailCanvasMounted(canvas) {
        console.log(`${this.LOG_PREFIX} setLiveSceneThumbnailCanvasMounted`, canvas ? 'mount' : 'unmount');
        if (canvas) {
            await this.videoPlayerWorkerProxy?.setLiveSceneThumbnailCanvasMounted(transfer(canvas, [canvas]));
        }
        else {
            await this.videoPlayerWorkerProxy?.setLiveSceneThumbnailCanvasMounted();
        }
    }
    async setReferenceTimes(trackIds, presentationTimeMicros, realTimeMillis) {
        await this.videoDecoderWorkerProxy?.setReferenceTimes(trackIds, presentationTimeMicros, realTimeMillis);
    }
    async reportUnderflow(trackIds) {
        await this.videoPlayerWorkerProxy?.reportUnderflow(trackIds);
    }
    async setAudioDriven(trackIds, enabled) {
        await this.videoPlayerWorkerProxy?.setAudioDriven(trackIds, enabled);
    }
    async getClockDiagnostics(trackIds) {
        return ((await this.videoPlayerWorkerProxy?.getClockDiagnostics(trackIds)) ?? []);
    }
    /**
     * Start the full player+decoder pipeline in direct rendering mode.
     * The player worker creates a DirectVideoRenderer that writes PTS-selected
     * frames to a VideoTrackGenerator (with PlaybackClock-driven A/V sync).
     * Returns a MediaStreamTrack for display in a <video> element.
     */
    async enableDirectVideoRendering() {
        console.log(`${this.LOG_PREFIX} enableDirectVideoRendering`);
        await this.startVideoPlayerWorker();
        await this.startVideoDecoderWorker();
        if (!this.videoPlayerWorkerProxy) {
            throw new Error('videoPlayerWorker not initialized');
        }
        const track = await this.videoPlayerWorkerProxy.enableDirectVideoRendering();
        console.log(`${this.LOG_PREFIX} enableDirectVideoRendering: track obtained`);
        return track;
    }
    /** @deprecated Use enableDirectVideoRendering() instead */
    async enableDirectVideoOutput() {
        return this.enableDirectVideoRendering();
    }
    /** @deprecated No longer needed — use stopVideoDecoderWorker + stopVideoPlayerWorker */
    async disableDirectVideoOutput() {
        await this.stopVideoDecoderWorker();
        await this.stopVideoPlayerWorker();
    }
    async feedEncodedVideoFrame(frame) {
        if (frame.done) {
            console.log(`${this.LOG_PREFIX} feedEncodedVideoFrame done`, {
                frame,
            });
        }
        const transferables = [];
        if (frame.data.byteLength > 0) {
            transferables.push(frame.data.buffer);
        }
        if (frame.extradata.byteLength > 0 &&
            frame.extradata.buffer !== frame.data.buffer) {
            transferables.push(frame.extradata.buffer);
        }
        if (transferables.length > 0) {
            await this.videoDecoderWorkerProxy?.feedEncodedVideoFrame(transfer(frame, transferables));
        }
        else {
            await this.videoDecoderWorkerProxy?.feedEncodedVideoFrame(frame);
        }
    }
    async handleLeave(userSessionId) {
        console.log(`${this.LOG_PREFIX} handleLeave`, { userSessionId });
        await this.videoDecoderWorkerProxy?.handleLeave(userSessionId);
    }
    // ========== Composite encoding ==========
    async startRenderedFrameCapture(timestampOffsetMicros = 0n) {
        if (!this.videoEncoderWorkerProxy || !this.videoPlayerWorkerProxy) {
            throw new Error('Both videoEncoderWorker and videoPlayerWorker must be running');
        }
        let resolveEncodedTrack;
        const encodedTrackPromise = new Promise((resolve) => {
            resolveEncodedTrack = resolve;
        });
        const { port1, port2 } = new MessageChannel();
        await this.videoEncoderWorkerProxy.addCompositeEncoder('viewer', resolveVideoCodecs(Settings.viewerStreamVideoCodecs), Settings.viewerStreamVideoBitrate, transfer(port1, [port1]), proxy(async (encodedTrackProxy) => {
            resolveEncodedTrack(encodedTrackProxy);
        }));
        await this.videoPlayerWorkerProxy.startRenderedFrameCapture(transfer(port2, [port2]), timestampOffsetMicros);
        const outputTrack = await encodedTrackPromise;
        console.log(`${this.LOG_PREFIX} startRenderedFrameCapture`);
        return outputTrack;
    }
    async stopRenderedFrameCapture() {
        try {
            await this.videoPlayerWorkerProxy?.stopRenderedFrameCapture();
        }
        catch (error) {
            console.warn(`${this.LOG_PREFIX} playerWorker.stopRenderedFrameCapture failed`, error);
        }
        try {
            await this.videoEncoderWorkerProxy?.removeCompositeEncoder('viewer');
        }
        catch (error) {
            console.warn(`${this.LOG_PREFIX} removeCompositeEncoder viewer failed`, error);
        }
        console.log(`${this.LOG_PREFIX} stopRenderedFrameCapture`);
    }
    async startRtmpCapture(rtmpBridgePort) {
        if (!this.videoEncoderWorkerProxy || !this.videoPlayerWorkerProxy) {
            throw new Error('Both videoEncoderWorker and videoPlayerWorker must be running');
        }
        const { port1, port2 } = new MessageChannel();
        await this.videoEncoderWorkerProxy.addCompositeEncoder('rtmp', Settings.rtmpVideoCodecs, Settings.rtmpVideoBitrate, transfer(port1, [port1]), proxy(async (encodedTrackProxy) => {
            const trackId = await encodedTrackProxy.getTrackId();
            console.log(`${this.LOG_PREFIX} RTMP encoded track available`, { trackId: trackId.slice(0, 8) });
            const listener = async (frame, done) => {
                if (frame && !done) {
                    try {
                        rtmpBridgePort.postMessage(frame, [
                            frame.data.buffer,
                        ]);
                    }
                    catch {
                        // Port may be closed
                    }
                }
                if (done) {
                    rtmpBridgePort.postMessage({ done: true });
                }
            };
            await encodedTrackProxy.setListener(proxy(listener));
        }));
        await this.videoPlayerWorkerProxy.startRtmpFrameCapture(transfer(port2, [port2]));
        console.log(`${this.LOG_PREFIX} startRtmpCapture`);
    }
    async stopRtmpCapture() {
        try {
            await this.videoPlayerWorkerProxy?.stopRtmpFrameCapture();
        }
        catch (error) {
            console.warn(`${this.LOG_PREFIX} playerWorker.stopRtmpFrameCapture failed`, error);
        }
        try {
            await this.videoEncoderWorkerProxy?.removeCompositeEncoder('rtmp');
        }
        catch (error) {
            console.warn(`${this.LOG_PREFIX} removeCompositeEncoder rtmp failed`, error);
        }
        console.log(`${this.LOG_PREFIX} stopRtmpCapture`);
    }
    // ========== Scene & display ==========
    async pinVideo(trackId) {
        await this.videoPlayerWorkerProxy?.pinVideo(trackId);
    }
    async unpinVideo() {
        await this.videoPlayerWorkerProxy?.unpinVideo();
    }
    async togglePinVideo(trackId) {
        await this.videoPlayerWorkerProxy?.togglePinVideo(trackId);
    }
    async getVideoAtPosition(x, y) {
        return ((await this.videoPlayerWorkerProxy?.getVideoAtPosition(x, y)) ??
            null);
    }
    async setDisplayScene(config) {
        await this.transferSceneToPlayer('setDisplayScene', config);
    }
    async setLiveScene(config) {
        await this.transferSceneToPlayer('setLiveScene', config);
    }
    async transferSceneToPlayer(method, config) {
        const transferables = collectSceneTransferables(config);
        if (transferables.length > 0) {
            await this.videoPlayerWorkerProxy?.[method](transfer(config, transferables));
        }
        else {
            await this.videoPlayerWorkerProxy?.[method](config);
        }
    }
    async getActiveTrackIds() {
        return (await this.videoPlayerWorkerProxy?.getActiveTrackIds()) ?? [];
    }
    async captureDisplayFrame(width, height) {
        return ((await this.videoPlayerWorkerProxy?.captureDisplayFrame(width, height)) ?? '');
    }
    async setThumbnailRenderingConfig(entries) {
        const transferables = [];
        for (const entry of entries) {
            if (entry.canvas) {
                transferables.push(entry.canvas);
            }
        }
        if (!this.videoPlayerWorkerProxy) {
            if (transferables.length > 0) {
                throw new Error('Cannot transfer thumbnail canvases: worker proxy not ready');
            }
            return;
        }
        if (transferables.length > 0) {
            await this.videoPlayerWorkerProxy.setThumbnailRenderingConfig(transfer(entries, transferables));
        }
        else {
            await this.videoPlayerWorkerProxy.setThumbnailRenderingConfig(entries);
        }
    }
    async addVideoTrack(track) {
        await this.videoPlayerWorkerProxy?.addVideoTrack(proxy(track));
    }
    async setExcludeLocalTracksFromMainCanvas(exclude) {
        await this.videoPlayerWorkerProxy?.setExcludeLocalTracksFromMainCanvas(exclude);
    }
    setFocusRemoteTrackId(trackId) {
        this.videoPlayerWorkerProxy?.setFocusRemoteTrackId(trackId);
    }
    async setScreenOrientation(type, _angle) {
        const canvasRotation = orientationTypeToRotationMap[type] ?? 0;
        await this.videoPlayerWorkerProxy?.setCanvasRotation(canvasRotation);
    }
    // ========== TimerWorker lifecycle ==========
    async ensureTimerWorker() {
        if (this.timerWorkerProxy) {
            return;
        }
        console.log(`${this.LOG_PREFIX} creating TimerWorker`);
        this.timerWorker = this.createTimerWorker();
        this.timerWorkerProxy = wrap(this.timerWorker);
        await this.timerWorkerProxy.start();
        console.log(`${this.LOG_PREFIX} TimerWorker created and started`);
    }
    async wireTimerPort(target) {
        await this.ensureTimerWorker();
        if (!this.timerWorkerProxy) {
            return;
        }
        const channel = new MessageChannel();
        await this.timerWorkerProxy.connectPort(transfer(channel.port1, [channel.port1]));
        if (target === 'player' && this.videoPlayerWorkerProxy) {
            await this.videoPlayerWorkerProxy.connectTimerPort(transfer(channel.port2, [channel.port2]));
        }
        else if (target === 'encoder' && this.videoEncoderWorkerProxy) {
            await this.videoEncoderWorkerProxy.connectTimerPort(transfer(channel.port2, [channel.port2]));
        }
        console.log(`${this.LOG_PREFIX} TimerWorker port wired to ${target} worker`);
    }
    destroyTimerWorker() {
        if (this.timerWorkerProxy) {
            this.timerWorkerProxy.stop();
        }
        this.timerWorker?.terminate();
        this.timerWorkerProxy = undefined;
        this.timerWorker = undefined;
    }
    async getCurrentTime() {
        if (!this.timerWorkerProxy) {
            return performance.now();
        }
        return this.timerWorkerProxy.getCurrentTime();
    }
    // ========== Worker creation ==========
    createPlayerWorker() {
        if (import.meta.env.BUILD_TRANSFORMS_FILENAMES) {
            return new Worker(new URL('./VideoPlayerWorker.worker.ts', import.meta.url), { type: 'module' });
        }
        const ext = import.meta.env.DEV ? '.ts' : '.js';
        return createWorker(`./VideoPlayerWorker.worker${ext}`, import.meta.url);
    }
    createEncoderWorker() {
        if (import.meta.env.BUILD_TRANSFORMS_FILENAMES) {
            return new Worker(new URL('./VideoEncoderWorker.ts', import.meta.url), { type: 'module' });
        }
        const ext = import.meta.env.DEV ? '.ts' : '.js';
        return createWorker(`./VideoEncoderWorker${ext}`, import.meta.url);
    }
    createTimerWorker() {
        if (import.meta.env.BUILD_TRANSFORMS_FILENAMES) {
            return new Worker(new URL('./TimerWorker.worker.ts', import.meta.url), { type: 'module' });
        }
        const ext = import.meta.env.DEV ? '.ts' : '.js';
        return createWorker(`./TimerWorker.worker${ext}`, import.meta.url);
    }
    createDecoderWorker() {
        if (import.meta.env.BUILD_TRANSFORMS_FILENAMES) {
            return new Worker(new URL('./VideoDecoderWorker.ts', import.meta.url), { type: 'module' });
        }
        const ext = import.meta.env.DEV ? '.ts' : '.js';
        return createWorker(`./VideoDecoderWorker${ext}`, import.meta.url);
    }
}
